import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'
import ActivityProgressBar from '../../../components/ActivityProgressBar'
import Breadcrumbs, { ICrumb } from '../../../components/BreadCrumbs'
import Button from '../../../components/Button'
import { BoldText } from '../../../components/Text'
import uiStore from '../../../stores/uiStore'
import { colors, device, spacing } from '../../../styles/_var'
import { routes } from '../../../utils/constants'
import CompetitionDetails from './CompetitionDetails'
import ContestantDetails from './ContestantDetails'
import Summary from './Summary'
import PayoutOptions from './PayoutOptions'
import contestantStore from '../../../stores/contestantStore'
import SeasonTypeSelector from '../../../components/SeasonTypeSelector'
import { disabledNextResolver, disabledPreviousResolver } from './utils'
import usePageTitle from '../../../hooks/usePageTitle'
import createCompetitionStore from '../../../stores/createCompetitionStore'


const CreateCompetition = () => {
  const { 
    currentStep, 
    stepTitle, 
    totalSteps, 
    hidePrevButton, 
    hideNextButton,
    nextStep
  } = createCompetitionStore
  const steps = createCompetitionStore.steps
  usePageTitle('Create Campaign')

  useEffect(() => {
    uiStore.setNavTitle('Create Campaign')
    createCompetitionStore.loadExistingCompetition()
  }, [])

  return (
    <Wrapper>
      <PageBreadCrumbs crumbs={breadcrumbdata} />
      <PageTitle variant="medium">{stepTitle}</PageTitle>
      <PageContent>
        <ActivityProgressBar currentStep={currentStep} totalSteps={totalSteps} />
        <ContentArea>
          {(() => {
            switch (currentStep) {
              case steps.COMPETITION_DETAILS:
                return (
                  <CompetitionDetails
                    initialValues={createCompetitionStore.competitionDetails}
                    createCompetition={data => createCompetitionStore.createCompetition(data)}
                    loading={createCompetitionStore.createCompetitionLoading}
                  />
                )
              case steps.CONTESTANT_DETAILS:
                return (
                  <ContestantDetails
                    competitionUsername={createCompetitionStore.competitionDetails.username}
                    seasonId={createCompetitionStore.competitionDetails.uuid || ''}
                    saveContestant={createCompetitionStore.saveContestant}
                    savingContestant={contestantStore.savingContestant}
                    contestants={contestantStore.contestants} />
                )
              case steps.SEASON_TYPE_CHOOSER:
                return (
                  <SeasonTypeSelector
                    onChange={createCompetitionStore.onSubscriptionTypeSelected}
                    subscriptionPlan={createCompetitionStore.competitionDetails.pricing_subscription}
                    disabled={createCompetitionStore.competitionDetails.paid}
                    overlayStamp={createCompetitionStore.competitionDetails.paid ? 'PAID' : ''}
                    controlled
                  />
                )
              case steps.PAYMENT_OPTIONS:
                return (
                  <PayoutOptions
                    initialValues={createCompetitionStore.data.payoutOptions}
                    submit={payload => createCompetitionStore.savePayoutOptions(payload)}
                    saving={createCompetitionStore.savePayoutOptionsLoading}
                  />
                )
              case steps.SUMMARY:
                return (
                  <Summary
                    payoutOptions={createCompetitionStore.data.payoutOptions}
                    contestants={contestantStore.contestants}
                    competitionDetails={createCompetitionStore.competitionDetails}
                    resetCompetitionFields={() => createCompetitionStore.resetCompetitionFields()} />
                )
              default:
                return null
            }
          })()}
        </ContentArea>
        <ButtonWrapper>
          <Button
            style={{ opacity: hidePrevButton ? 0 : undefined}}
            variant="secondary"
            disabled={disabledPreviousResolver(currentStep)}
            onClick={() => createCompetitionStore.setCurrentStep(currentStep - 1)}
          >
            &lt;&lt; Prev
          </Button>
          <Button
            style={{ opacity: hideNextButton ? 0 : undefined}}
            variant="secondary" 
            disabled={disabledNextResolver(currentStep)}
            onClick={nextStep}
          >
            Next &gt;&gt;
          </Button>
        </ButtonWrapper>
      </PageContent>
    </Wrapper>
  )
}

export default observer(CreateCompetition)

const ContentArea = styled.div`
  box-sizing: border-box;
  padding: 0 ${spacing.sm};
  max-width: 100rem;
  margin: auto;
`

const PageBreadCrumbs = styled(({ ...rest }) => <Breadcrumbs {...rest} />)`
  position: relative;
  top: -2rem;
`

const Wrapper = styled.div`
  margin: ${spacing.xxs} ${spacing.sm};
  position: relative;
`

const breadcrumbdata: ICrumb[]  = [
  { name: 'My Campaigns', route: routes.MY_COMPETITIONS },
  { name: 'Create Campaign' }
]

const PageTitle = styled(BoldText)`
  margin-bottom: ${spacing.xs};
  text-transform: uppercase;

  @media ${device.tablet} {
    margin-bottom: ${spacing.sm};
  }
`

const PageContent = styled.div`
  border: 0.5px solid ${colors.lightgrey};
  border-radius: 1rem;
  padding: ${spacing.xs};

  & > div:first-child {
    margin-bottom: ${spacing.sm};
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${spacing.sm};
`